import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';
import { fmtInt } from './_finHelpers';

// Rent Calculator — mirrors reference image
// Two inputs: pre-tax income (with per-year/per-month) + monthly debt payback
// Uses 28% (recommended) / 36% (max) / 33% (landlord 1/3 rule) DTI standards
export default function RentCalc() {
  const [income, setIncome] = useState(80000);
  const [incomeFreq, setIncomeFreq] = useState('year');
  const [debt, setDebt] = useState(0);

  const c = useMemo(() => {
    const monthlyIncome = incomeFreq === 'year' ? income / 12 : income;
    const recommended = Math.max(0, monthlyIncome * 0.28 - debt); // 28% front-end
    const maxAfford = Math.max(0, monthlyIncome * 0.36 - debt);    // 36% back-end
    const oneThird = Math.max(0, monthlyIncome / 3 - debt);        // 1/3 landlord rule
    return { monthlyIncome, recommended, maxAfford, oneThird };
  }, [income, incomeFreq, debt]);

  return (
    <CalcShell look="cnet" title="Rent Calculator" subtitle="How Much Rent Can I Afford?"
      about={<>
        <p>The <b>Rent Calculator</b> estimates the maximum rent you can afford. It applies conventional debt-to-income (DTI) standards: 28% of gross monthly income is a comfortable rent (front-end DTI), 36% is the upper bound (back-end DTI), and 33% is the "one-third of gross income" rule many landlords use.</p>
      </>}
      slug="rent"
    >
      {/* Result on top */}
      <div className="rounded-xl border border-slate-200 overflow-hidden mb-6">
        <div className="bg-emerald-700 text-white px-4 py-2 font-bold">Result</div>
        <div className="p-5">
          <p className="text-slate-800 text-lg mb-4">
            You can afford up to <b className="text-emerald-700 text-2xl" data-testid="rent-max">{fmtInt(c.maxAfford)}</b> per month on a rental payment.
            It is recommended to keep your rental payment below <b className="text-emerald-700 text-2xl" data-testid="rent-recommended">{fmtInt(c.recommended)}</b> per month.
          </p>

          {/* Safe / Acceptable / Aggressive gauge */}
          <RentGauge recommended={c.recommended} maxAfford={c.maxAfford} />

          <p className="text-slate-700 text-sm mt-4">
            Some landlords may not accept applications with more than 1/3 of gross income on rent, which is <b>{fmtInt(c.oneThird)}</b>.
          </p>
        </div>
      </div>

      {/* Inputs */}
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-slate-700 mb-1">Your pre-tax income</label>
            <div className="flex gap-2">
              <div className="flex flex-1 border border-slate-300 rounded overflow-hidden">
                <span className="px-2 bg-slate-100 border-r border-slate-300 self-stretch flex items-center text-slate-500">$</span>
                <input type="number" value={income} onChange={e=>setIncome(+e.target.value||0)} className="flex-1 px-2 py-1 outline-none" data-testid="rent-income"/>
              </div>
              <select value={incomeFreq} onChange={e=>setIncomeFreq(e.target.value)} className="border border-slate-300 rounded px-2 py-1" data-testid="rent-freq">
                <option value="year">per year</option>
                <option value="month">per month</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm text-slate-700 mb-1">
              Your monthly debt payback <span className="text-slate-400 text-xs" title="car/student loan, credit cards, etc">(?)</span>
            </label>
            <div className="flex gap-2 items-center">
              <div className="flex flex-1 border border-slate-300 rounded overflow-hidden">
                <span className="px-2 bg-slate-100 border-r border-slate-300 self-stretch flex items-center text-slate-500">$</span>
                <input type="number" value={debt} onChange={e=>setDebt(+e.target.value||0)} className="flex-1 px-2 py-1 outline-none" data-testid="rent-debt"/>
              </div>
              <span className="text-xs text-slate-500 whitespace-nowrap">car/student loan, credit cards, etc</span>
            </div>
          </div>
        </div>
      </div>
    </CalcShell>
  );
}

function RentGauge({ recommended, maxAfford }) {
  // The gauge is a horizontal bar: green (safe) / yellow (acceptable) / red (aggressive)
  // recommended sits at boundary green→yellow, maxAfford sits at boundary yellow→red
  const rangeEnd = Math.max(1, maxAfford * 1.35);
  const p1 = Math.min(100, (recommended / rangeEnd) * 100);
  const p2 = Math.min(100, (maxAfford / rangeEnd) * 100);
  return (
    <div className="relative w-full max-w-md mx-auto pt-8 pb-2">
      <div className="absolute left-0 right-0 -top-1 flex text-sm font-bold">
        <span className="text-emerald-700" style={{ position: 'absolute', left: `calc(${p1}% - 32px)` }}>{fmtInt(recommended)}</span>
        <span className="text-slate-900" style={{ position: 'absolute', left: `calc(${p2}% - 32px)` }}>{fmtInt(maxAfford)}</span>
      </div>
      <div className="relative h-8 rounded overflow-hidden flex text-white text-xs font-semibold">
        <div className="flex items-center justify-center bg-emerald-600" style={{ width: `${p1}%` }}>safe</div>
        <div className="flex items-center justify-center bg-yellow-400 text-slate-900" style={{ width: `${p2 - p1}%` }}>acceptable</div>
        <div className="flex items-center justify-center bg-red-600" style={{ width: `${100 - p2}%` }}>aggressive</div>
        {/* Markers */}
        <div className="absolute top-0 bottom-0 w-0.5 bg-black" style={{ left: `${p1}%` }} />
        <div className="absolute top-0 bottom-0 w-0.5 bg-black" style={{ left: `${p2}%` }} />
      </div>
    </div>
  );
}
